import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { FavouritesModal, Gamecard } from '@digitalmedia/sn-design-system'
import BigBox from 'components/ad_types/BigBox'
import Leaderboard from 'components/ad_types/Leaderboard'
import SponsorBadge from 'components/ad_types/SponsorBadge'
import Feedbackify from 'components/feedback/Feedback'
import DatePicker from 'components/date_picker/DatePicker'
import store from 'store'

import { formatGamecardData as formatMLBGamecardData } from 'utils/js/mlb_helpers'
import { formatGamecardData as formatNHLGamecardData } from 'utils/js/nhl_helpers'
import { formatGamecardData as formatNBAGamecardData } from 'utils/js/nba_helpers'
import { formatGamecardData as formatNFLGamecardData } from 'utils/js/nfl_helpers'
import { formatGamecardData as formatCFLGamecardData } from 'utils/js/cfl_helpers'
import { formatGamecardData as formatNLLGamecardData } from 'utils/js/nll_helpers'
import { formatGamecardData as formatSoccerGamecardData } from 'utils/js/soccer_helpers'
import { getDatetime, parseDashedDate } from 'utils/js/time_helpers'
import { calendarLabel } from 'utils/js/shared_helpers'
import {
    stats_v3,
    personalization_url,
    eas_authorization_path,
    eas_client_id,
    eas_redirect_url,
    eas_secure_domain,
} from 'utils/js/endpoints'
import useConfig from 'context/ConfigContext'
import useUser from 'context/UserContext'
import { renderScoresPageHelmet } from './scores_helpers'
import { formatDateWithTimezone } from 'utils/js/time_helpers'
import { randomString } from 'utils/js/browser_helpers'

import './Scores.scss'

const Scores = ({ match, history }) => {
    const fetchIntervalTime = 10000
    const initialDate = parseDashedDate(match?.params?.day)
    const [supportedLeagues, setSupportedLeagues] = useState([])
    const [selectedLeague, setSelectedLeague] = useState()
    const [isLoadingScores, setIsLoadingScores] = useState(true)
    const [games, setGames] = useState([])
    const [error, setError] = useState([])
    const [adIterator, setAdIterator] = useState(0)
    const [showPersonanlizationModal, setShowPersonanlizationModal] = useState(false)
    const [seasonLabel, setSeasonLabel] = useState()
    const [date, setDate] = useState(initialDate)

    const { favourites, isLoadingFavourites, refreshFavourites, user } = useUser()
    const config = useConfig()

    const hasFavourites = !!Object.keys(favourites).length

    useEffect(() => {
        if (config.loading) return
        const leagues = [
            {
                name: 'Favourites',
                slug: 'favourites',
                sport: '',
            },
            ...config.activeLeagues.filter(league => league.activeScores),
        ]

        setSupportedLeagues(leagues)
        setSelectedLeague(leagues[0])
    }, [config])

    useEffect(() => {
        if (config.loading) return
        if (!selectedLeague) return

        const dashedDate = match?.params?.day
            ? match?.params?.day
            : checkRolloverTime(new Date().getTime() / 1000)

        setDate(parseDashedDate(dashedDate))

        if (selectedLeague.slug === 'favourites') {
            fetchFavouriteGames(favourites, dashedDate, false)
            const fetchIntervalId = setInterval(
                () => fetchFavouriteGames(favourites, dashedDate, true),
                fetchIntervalTime
            )
            return () => {
                clearInterval(fetchIntervalId)
            }
        } else {
            loadScoresData(selectedLeague.slug, dashedDate, false)
            const fetchIntervalId = setInterval(
                () => loadScoresData(selectedLeague.slug, dashedDate, true),
                fetchIntervalTime
            )

            return () => {
                clearInterval(fetchIntervalId)
            }
        }
    }, [selectedLeague, match?.params?.day, favourites])

    const fetchFavouriteGames = async (favouritesByLeague, day, isPolling) => {
        if (!isPolling) {
            setIsLoadingScores(true)
            setError(false)
            setAdIterator(oldVal => oldVal + 1)
        }

        const promises = Object.keys(favouritesByLeague).map(league => {
            if (
                !supportedLeagues.find(
                    supportedLeague => supportedLeague?.slug?.toLowerCase() === league
                )
            )
                return Promise.resolve()

            return new Promise(async (resolve, reject) => {
                const favouriteTeamIds = favouritesByLeague[league].map(
                    favouriteTeam => favouriteTeam.sr_id
                )

                const statsUrl = stats_v3
                const leagueGamesResponse = await fetch(
                    `${statsUrl}/ticker?league=${league}&day=${day}`
                ).catch(err => {
                    reject(err)
                })

                if (leagueGamesResponse && leagueGamesResponse.ok) {
                    const scoresData = await leagueGamesResponse.json()
                    const leagueGames = scoresData?.data?.games || []

                    const filteredGames = leagueGames.filter(
                        game =>
                            favouriteTeamIds.includes(`${game.visiting_team.id}`) ||
                            favouriteTeamIds.includes(`${game.home_team.id}`)
                    )

                    resolve(filteredGames)
                }
                resolve()
            })
        })

        const results = await Promise.allSettled(promises)
        const favouriteGames = results
            .map(result => (result.status === 'fulfilled' && result.value) || [])
            .flat()

        setGames(favouriteGames)
        setIsLoadingScores(false)
    }

    const loadScoresData = async (league, day, isPolling) => {
        if (!isPolling) {
            setIsLoadingScores(true)
            setError(false)
            setAdIterator(oldVal => oldVal + 1)
        }

        const statsUrl = stats_v3

        const response = await fetch(`${statsUrl}/ticker?league=${league}&day=${day}`).catch(() => {
            setError('Something went wrong. Please try again later.')
        })

        if (response && response.ok) {
            const scoresData = await response.json()

            const games = scoresData?.data?.games || []

            const sortedGames = games
                .map(game => {
                    const isCompleted = game.game_status === 'Final'
                    return {
                        ...game,
                        isCompleted,
                    }
                })
                .sort((a, b) => {
                    if (a.isCompleted) return 1
                    if (b.isCompleted) return -1
                })

            let seasonType = calendarLabel(games[0]?.type, games[0]?.league)
            setSeasonLabel(seasonType)
            setGames(sortedGames)
        } else {
            setError('Something went wrong. Please try again later.')
        }
        setIsLoadingScores(false)
    }

    const onDateChange = (date, usingCalendar = false) => {
        const unixDate = date.getTime() / 1000
        const dashedDate = getDatetime(unixDate, 'dashed')
        const slashedDate = getDatetime(unixDate, 'month/day/year')
        history.replace(`/scores/${dashedDate}`)

        store.dispatch({
            type: 'ANALYTIC_EVENT_SCORES_PAGE_ACTION',
            name: 'scores_page_datepicker',
            json: {
                actionName: 'date selection',
                dateSelected: slashedDate,
                method: usingCalendar ? 'calendar' : 'chevron',
            },
        })
    }

    const trackGamecardClick = (rawGame, formattedGame) => {
        store.dispatch({
            type: 'ANALYTIC_EVENT_SCORES_PAGE_ACTION',
            name: 'scores_page_action',
            json: {
                actionName: 'game selected',
                livetrackerUrl: formattedGame.link,
                gameStatus: rawGame.game_status,
                gameSchedule: formatDateWithTimezone(rawGame.timestamp).join(' '),
                gameId: rawGame.id,
            },
        })
    }

    const gamesSort = games => {
        games.sort((a, b) => a.timestamp - b.timestamp)
        return games
    }

    const checkRolloverTime = date => {
        let rollOverDate = new Date()
        rollOverDate.setHours(11)
        rollOverDate.setMinutes(0)
        rollOverDate.setSeconds(0)
        const rolloverTime = Math.floor(rollOverDate / 1000)

        if (date < rolloverTime) {
            let newDate = new Date()
            newDate.setDate(newDate.getDate() - 1)
            newDate = getDatetime(newDate.getTime() / 1000, 'dashed')
            return newDate
        } else {
            return getDatetime(new Date().getTime() / 1000, 'dashed')
        }
    }

    const formatGames = gamesData => {
        const games = gamesSort(gamesData)
        const formattedGames = games.map(game => {
            const timestamp = game.timestamp
            // eslint-disable-next-line no-undef
            const reformattedGame = reformatGameParams(game)
            const isCompleted = game.game_status === 'Final'
            const visitingTeamId = game.visiting_team.id
            const homeTeamId = game.home_team.id
            const league = game.league
            const additionalGameData = {
                isCompleted,
                visitingTeamId,
                homeTeamId,
                league,
                timestamp,
            }

            const formatFunctions = {
                mlb: formatMLBGamecardData,
                wbc: formatMLBGamecardData,
                nba: formatNBAGamecardData,
                nfl: formatNFLGamecardData,
                cfl: formatCFLGamecardData,
                wnba: formatNBAGamecardData,
                nll: formatNLLGamecardData,
                soccer: formatSoccerGamecardData,
                hockey: formatNHLGamecardData,
                juniors: formatNHLGamecardData,
                'hockey,juniors': formatNHLGamecardData,
            }

            const formatFunction = formatFunctions[game.league]
                ? formatFunctions[game.league]
                : formatFunctions[game.sport]
                ? formatFunctions[game.sport]
                : null
            if (formatFunction) {
                return {
                    ...formatFunction(reformattedGame),
                    ...additionalGameData,
                }
            }
            return {}
        })

        // Float completed games to the bottom of the list.
        formattedGames.sort((a, b) => (a.isCompleted ? 1 : b.isCompleted ? -1 : 0))

        const favouriteTeamIds =
            favourites[selectedLeague?.slug]?.map(team =>
                selectedLeague?.slug === 'nhl' ? team.sr_id : team.stats_id
            ) || [] // SR_MIGRATION

        // Float favourite team's games to the top of the list
        formattedGames.sort((a, b) => {
            const isFavouriteTeam = game =>
                favouriteTeamIds.some(
                    id =>
                        id === `${game.league}.${game.visitingTeamId}` ||
                        id === `${game.league}.${game.homeTeamId}` ||
                        id === game.homeTeamId ||
                        id === game.visitingTeamId
                )

            return isFavouriteTeam(a) && !isFavouriteTeam(b)
                ? -1
                : !isFavouriteTeam(a) && isFavouriteTeam(b)
                ? 1
                : isFavouriteTeam(a) && isFavouriteTeam(b)
                ? a.timestamp - b.timestamp
                : 0
        })

        return formattedGames
    }

    const noGamesString = `There are no ${selectedLeague?.name} games on ${moment(
        match?.params?.day
    ).format('dddd, MMMM D')}.`
    const noFavouriteGamesString = `There are no games on ${moment(match?.params?.day).format(
        'dddd, MMMM D'
    )} for your selected favourites.`

    const favouritesTabActive = selectedLeague?.slug === 'favourites'
    const loading = isLoadingScores || isLoadingFavourites

    return (
        <>
            <div className="scores-page">
                {renderScoresPageHelmet()}

                <div className="title__container">
                    <div className="title">SCORES</div>
                    <div className="sponsor__cont">
                        <SponsorBadge targetMode="adUnit" />
                    </div>
                </div>

                <DatePicker date={date} onDateChange={onDateChange} />

                <div className="league_tabs__wrapper">
                    <div className="league_tabs__container">
                        {supportedLeagues.map(league => {
                            const isSelected = league.slug === selectedLeague?.slug
                            return (
                                <div
                                    key={league.slug}
                                    className={`tab ${isSelected ? 'selected' : null}`}
                                    onClick={() => setSelectedLeague(league)}
                                >
                                    {league.name}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className="scores_content__container">
                    {seasonLabel && <div className="game_type">{seasonLabel}</div>}
                    <div className="gamecards__container">
                        {loading && (
                            <>
                                <div className="gamecard__wrapper">
                                    <Gamecard />
                                </div>
                                <div className="gamecard__wrapper">
                                    <Gamecard />
                                </div>
                                <div className="gamecard__wrapper">
                                    <Gamecard />
                                </div>
                            </>
                        )}

                        {!loading &&
                            games.length > 0 &&
                            formatGames(games).map((gamecardData, index) => {
                                if (index === 3) {
                                    // include an ad after the 3rd gamecard
                                    return (
                                        <>
                                            <div
                                                key={gamecardData.link}
                                                className="gamecard__wrapper"
                                                onClick={() =>
                                                    trackGamecardClick(games[index], gamecardData)
                                                }
                                            >
                                                <Gamecard {...gamecardData} />
                                            </div>
                                            <div className="ad__wrapper visible-xs">
                                                <BigBox
                                                    bg={false}
                                                    view={'mobile'}
                                                    refresh={true}
                                                    visible={true}
                                                    index={adIterator}
                                                />
                                            </div>
                                        </>
                                    )
                                } else {
                                    return (
                                        <div
                                            key={gamecardData.link}
                                            className="gamecard__wrapper"
                                            onClick={() =>
                                                trackGamecardClick(games[index], gamecardData)
                                            }
                                        >
                                            <Gamecard {...gamecardData} />
                                        </div>
                                    )
                                }
                            })}

                        {!loading &&
                        games.length > 0 && // Add blank gamecards to even out the grid.
                            Array.apply(null, Array(3 - (games.length % 3))).map((game, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="gamecard__wrapper"
                                        style={{ opacity: 0, height: 0, margin: 0 }}
                                    >
                                        <Gamecard />
                                    </div>
                                )
                            })}

                        {!loading && !error && games.length === 0 && !favouritesTabActive && (
                            <div className="no_games">{noGamesString}</div>
                        )}
                        {!loading &&
                            !error &&
                            games.length === 0 &&
                            !hasFavourites &&
                            favouritesTabActive && (
                                <div className="no_games">
                                    {
                                        <div className="favourites_instructions__cont">
                                            <>
                                                <img
                                                    src={require('./stars.svg')}
                                                    className="favourites_icon"
                                                />
                                                <div className="favourites_instructions">
                                                    Set your favourite teams and leagues to see
                                                    their scores up first
                                                </div>

                                                <div
                                                    className="favourites_button"
                                                    onClick={() =>
                                                        setShowPersonanlizationModal(true)
                                                    }
                                                >
                                                    Add Favourites
                                                </div>
                                            </>
                                        </div>
                                    }
                                </div>
                            )}
                        {!loading &&
                            !error &&
                            games.length === 0 &&
                            favouritesTabActive &&
                            hasFavourites && (
                                <div className="no_games">{noFavouriteGamesString}</div>
                            )}
                        {!loading && error && <div className="error_text">{error}</div>}
                    </div>

                    <div className="LeaderboardAd__Wrapper hidden-xs">
                        <Leaderboard
                            view={'all'}
                            unMounting={false}
                            index={adIterator}
                            visible={true}
                            refresh={true}
                        />
                    </div>

                    {!loading && favouritesTabActive && (games.length || hasFavourites) > 0 && (
                        <div className="favourites_instructions__cont">
                            <div
                                className="favourites_button"
                                onClick={() => setShowPersonanlizationModal(true)}
                            >
                                {hasFavourites ? 'Edit Favourites' : 'Add Favourites'}
                            </div>
                        </div>
                    )}

                    {games.length < 4 && !loading && (
                        <div className="ad__wrapper visible-xs">
                            <BigBox
                                bg={false}
                                view={'mobile'}
                                refresh={true}
                                visible={true}
                                index={adIterator}
                            />
                        </div>
                    )}

                    {!loading && <Feedbackify formId="16243" />}
                </div>
            </div>
            <FavouritesModal
                show={showPersonanlizationModal}
                userUUID={user.guid}
                apiToken={user.easFlatToken}
                apiUrl={personalization_url}
                accountCreationUrl={
                    !user.isLoggedIn
                        ? `${eas_secure_domain}/${eas_authorization_path}?client_id=${eas_client_id}&redirect_uri=${eas_redirect_url}&scope=openid offline_access&response_type=code&response_mode=query&nonce=${randomString(
                              16
                          )}`
                        : null
                }
                onModalClose={() => {
                    setShowPersonanlizationModal(false)
                    refreshFavourites()
                }}
            />
        </>
    )
}

export default Scores
